// ai-plan-suggest-button.tsx — "✨ Gợi ý MIT bằng AI" for the morning planning modal.
// Ranks open tasks into 1–3 MITs (respecting capacity/deadlines/goals). Opt-in;
// caller renders only when aiAvailable(). User still edits/reorders/saves as usual.

import { useState } from "react";
import { Loader2, Sparkles, AlertCircle } from "lucide-react";
import { todayIso } from "../lib/date";
import { getPlanningPreference } from "../lib/app-prefs";
import { createAiClient } from "../lib/ai/ai-runtime";
import { collectPlanCandidates, buildPlanRequest, mapPlanResult } from "../lib/ai/plan-suggest";
import type { PlanPick } from "../lib/ai/plan-suggest";
import type { LifeOsApi } from "../lib/life-os-contract";

interface Props {
  api: LifeOsApi;
  capacityMin: number;
  /** Called with validated, ordered picks (≤3). User reviews before saving. */
  onResult: (picks: PlanPick[]) => void;
}

type ButtonState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

export function AiPlanSuggestButton({ api, capacityMin, onResult }: Props) {
  const [state, setState] = useState<ButtonState>({ kind: "idle" });

  async function handleClick() {
    if (state.kind === "loading") return;
    const candidates = collectPlanCandidates(api.state);
    if (candidates.length === 0) {
      setState({ kind: "error", message: "Không có việc nào để gợi ý." });
      return;
    }
    setState({ kind: "loading" });
    try {
      const activeGoals = api.state.goals.filter((g) => g.status === "active").map((g) => g.title);
      const client = createAiClient();
      const raw = await client.generateJson<{ picks: PlanPick[] }>(
        buildPlanRequest(candidates, capacityMin, activeGoals, todayIso(), getPlanningPreference()),
      );
      const picks = mapPlanResult(raw, new Set(candidates.map((c) => c.id)));
      if (picks.length === 0) {
        setState({ kind: "error", message: "AI chưa chọn được — thử lại hoặc chọn tay." });
        return;
      }
      setState({ kind: "idle" });
      onResult(picks);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Không gợi ý được — vui lòng thử lại.";
      setState({ kind: "error", message });
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={state.kind === "loading"}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-[12px] font-semibold text-violet-700 transition hover:bg-violet-100 disabled:opacity-50 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50"
      >
        {state.kind === "loading" ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {state.kind === "loading" ? "AI đang chọn…" : "Gợi ý MIT bằng AI"}
      </button>

      {state.kind === "error" && (
        <div className="flex items-start gap-1.5 rounded-xl bg-rose-50 px-3 py-2 text-[11px] text-rose-600 dark:bg-rose-950/30 dark:text-rose-400">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{state.message}</span>
        </div>
      )}
    </div>
  );
}
